import { z } from 'zod';
import { CreateMediaUploadResponseSchema } from '../media/files.schemas';
import { refersTo } from '../common/refers-to';
import { PropertyMediaVideoLinkSchema } from './video-links';

/**
 * V1 upload policy (server-authoritative). Per-type allow-lists and caps: images
 * (JPEG, PNG, WebP; ≤ 20 MB) and videos (MP4; ≤ 200 MB). The consumer (property
 * module) branches on the declared content type to pick the right allow-list,
 * cap and German reason, then passes them to the media port — the media module
 * holds no policy of its own.
 */
export const MEDIA_IMAGE_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

export const MEDIA_VIDEO_CONTENT_TYPES = ['video/mp4'] as const;

export const MEDIA_IMAGE_MAX_SIZE_BYTES = 20 * 1024 * 1024;

export const MEDIA_VIDEO_MAX_SIZE_BYTES = 200 * 1024 * 1024;

/** Gallery cap per Objekt — enforced at presign (authoritative) and re-checked at confirm. */
export const MEDIA_GALLERY_CAP = 100;

export const MEDIA_MEDIA_DESCRIPTION = 'property-media';

/** Media kind, derived server-side from the MIME type. */
export const MediaTypeSchema = z.enum(['IMAGE', 'VIDEO']);

export type MediaType = z.infer<typeof MediaTypeSchema>;

/** Image sub-category (photo vs floor plan). Null for videos, which carry no category. */
export const MediaCategorySchema = z.enum(['PHOTO', 'FLOOR_PLAN']);

export type MediaCategory = z.infer<typeof MediaCategorySchema>;

export const PropertyMediaItemSchema = z.object({
  id: z.uuid(),
  type: MediaTypeSchema,
  category: MediaCategorySchema.nullable(),
  position: z.number().int().nonnegative(),
  isCoverImage: z.boolean(),
  // IMM-794 — whether this item is in the property's marketing set: the one
  // thing that decides whether it may leave the workspace. Default false; the
  // cover image is always true. An item outside the set is still an ordinary
  // gallery item on every read — only this flag is false.
  isPublished: z.boolean(),
  displayName: z.string(),
  originalFilename: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  uploadedByMemberId: refersTo('member', z.uuid()),
  // Null when the uploader is no longer an active workspace member; the frontend
  // renders the "unknown member" placeholder via i18n (no German in the backend).
  uploadedByName: z.string().nullable(),
  // Server-computed: may the current caller delete this item — true when they are
  // the uploader or hold `organization:property:media-manage-any` (Owner / Admin).
  // Drives the per-item delete action in the gallery; the server still re-checks.
  canManage: z.boolean(),
  uploadedAt: z.iso.datetime(),
  // Presigned inline GET URL for the ORIGINAL bytes, minted per request. Used by
  // the lightbox / video playback path; the grid uses `thumbnailUrl` instead.
  url: z.url(),
  urlExpiresInSeconds: z.number().int().positive(),
  // Presigned inline GET URL for the server-generated thumbnail (image) or poster
  // (video). Null while the derivative is still PENDING/PROCESSING or has FAILED —
  // the grid shows a placeholder until it is ready. Generated asynchronously.
  thumbnailUrl: z.url().nullable(),
  thumbnailExpiresInSeconds: z.number().int().positive().nullable(),
  // IMM-1165 — set on a YouTube or Vimeo video added by link, null on every upload. Such an item is
  // always a VIDEO in `videos`. Its `url`, `thumbnailUrl`, `contentType`, `sizeBytes` and
  // `originalFilename` describe immoOS's stored copy of the provider's preview image — the film itself
  // stays at the provider, at `videoLink.url`.
  videoLink: PropertyMediaVideoLinkSchema.nullable(),
});

export type PropertyMediaItem = z.infer<typeof PropertyMediaItemSchema>;

export const PropertyMediaPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

/** Gallery area a curation action targets (IMM-219.2). */
export const MediaAreaSchema = z.enum(['PHOTOS', 'FLOOR_PLANS', 'VIDEOS']);

export type MediaArea = z.infer<typeof MediaAreaSchema>;

/** Per-item path params: property + a specific media row within it. */
export const PropertyMediaItemPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
  mediaId: z.uuid(),
});

export const RenamePropertyMediaBodySchema = z
  .object({
    displayName: z.string().trim().min(1).max(255),
  })
  .strict();

export type RenamePropertyMediaBody = z.infer<typeof RenamePropertyMediaBodySchema>;

export const RecategorizePropertyMediaBodySchema = z
  .object({
    category: MediaCategorySchema,
  })
  .strict();

export type RecategorizePropertyMediaBody = z.infer<typeof RecategorizePropertyMediaBodySchema>;

export const ReorderPropertyMediaBodySchema = z
  .object({
    area: MediaAreaSchema,
    orderedIds: z.array(z.uuid()).min(1),
  })
  .strict();

export type ReorderPropertyMediaBody = z.infer<typeof ReorderPropertyMediaBodySchema>;

export const SetPropertyMediaPublishedBodySchema = z
  .object({
    isPublished: z.boolean(),
  })
  .strict();

export type SetPropertyMediaPublishedBody = z.infer<typeof SetPropertyMediaPublishedBodySchema>;

export const PublishPropertyMediaAreaBodySchema = z
  .object({
    area: MediaAreaSchema,
  })
  .strict();

export type PublishPropertyMediaAreaBody = z.infer<typeof PublishPropertyMediaAreaBodySchema>;

// Read mode → media content-disposition, mirroring the document read (IMM-218.2):
// `preview` mints an inline URL (the browser renders it), `download` mints an
// attachment URL (the browser saves it under the original filename). Default is
// `download`, the same default and for the same reason as the document read. Its
// own enum rather than an import of `DOCUMENT_READ_MODES`: the two happen to be
// the same pair today, and a shared symbol would make one surface's future mode
// (a poster frame, a variant) silently appear on the other.
export const MEDIA_READ_MODES = ['preview', 'download'] as const;

export type MediaReadMode = (typeof MEDIA_READ_MODES)[number];

export const DEFAULT_MEDIA_READ_MODE: MediaReadMode = 'download';

export const ReadPropertyMediaQuerySchema = z
  .object({
    mode: z.enum(MEDIA_READ_MODES).optional(),
  })
  .strict();

export type ReadPropertyMediaQuery = z.infer<typeof ReadPropertyMediaQuerySchema>;

export const ReadPropertyMediaResponseSchema = z.object({
  url: z.url(),
  expiresInSeconds: z.number().int().positive(),
});

export type ReadPropertyMediaResponse = z.infer<typeof ReadPropertyMediaResponseSchema>;

export const CreatePropertyMediaUploadBodySchema = z
  .object({
    contentType: z.string().min(1).max(255),
    sizeBytes: z.number().int().positive(),
    originalFilename: z.string().trim().min(1).max(255),
  })
  .strict();

export type CreatePropertyMediaUploadBody = z.infer<typeof CreatePropertyMediaUploadBodySchema>;

// The presign response is the media module's `CreateMediaUploadResponse` verbatim
// (S3 POST target + pending file id) — media adds no fields of its own here.
export const CreatePropertyMediaUploadResponseSchema = CreateMediaUploadResponseSchema;

export type CreatePropertyMediaUploadResponse = z.infer<typeof CreatePropertyMediaUploadResponseSchema>;

export const ConfirmPropertyMediaUploadBodySchema = z
  .object({
    fileId: z.uuid(),
  })
  .strict();

export type ConfirmPropertyMediaUploadBody = z.infer<typeof ConfirmPropertyMediaUploadBodySchema>;

export const ConfirmPropertyMediaUploadResponseSchema = PropertyMediaItemSchema;

export type ConfirmPropertyMediaUploadResponse = z.infer<typeof ConfirmPropertyMediaUploadResponseSchema>;

export const ListPropertyMediaResponseSchema = z.object({
  photos: z.array(PropertyMediaItemSchema),
  floorPlans: z.array(PropertyMediaItemSchema),
  videos: z.array(PropertyMediaItemSchema),
});

export type ListPropertyMediaResponse = z.infer<typeof ListPropertyMediaResponseSchema>;

/**
 * The per-free-text-field character count above which ImmoScout24 truncates a
 * description. Advisory ONLY: shown beside the counter and refuses nothing.
 *
 * IMM-794's provisional figure. The authoritative value belongs to the
 * ImmoScout24 integration (IMM-793), which is what actually transmits and
 * therefore what actually knows; when it lands it owns this constant.
 */
export const IMMOSCOUT24_DESCRIPTION_MAX_CHARACTERS = 3_500;
